import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import { useTheme, alpha } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PsychologyIcon from "@mui/icons-material/Psychology";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ProfSelector from "components/ProfSelector";
import {
  listQuizInsightsCall,
  createQuizInsightCall,
  deleteQuizInsightCall,
} from "../../redux/api/quizSystem.api";

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function QuizInsights() {
  const theme = useTheme();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [profId, setProfId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [scopeType, setScopeType] = useState("all");
  const [requestTitle, setRequestTitle] = useState("");

  const loadInsights = async () => {
    setLoading(true);
    try {
      const params = {};
      if (profId) params.prof_id = profId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (scopeType) params.scope_type = scopeType;
      const res = await listQuizInsightsCall(params);
      const data = res?.data ?? res;
      setInsights(Array.isArray(data) ? data : []);
    } catch (_) {
      setInsights([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInsights();
  }, [profId, scopeType, dateFrom, dateTo]);

  const handleRequestAnalysis = async () => {
    setCreating(true);
    try {
      const body = {
        scope_type: scopeType,
        title: requestTitle || undefined,
        prof_id: profId || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      await createQuizInsightCall(body);
      setRequestTitle("");
      await loadInsights();
    } catch (_) {
      // keep list as is
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteQuizInsightCall(id);
      setInsights((prev) => prev.filter((i) => i.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (_) {}
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const cardBg = theme.palette.background?.card ?? theme.palette.background.paper ?? theme.palette.background.default;
  const primaryMain = theme.palette.primary.main;
  const gradientBg = `linear-gradient(135deg, ${alpha(primaryMain, 0.08)} 0%, ${alpha(primaryMain, 0.02)} 100%)`;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        py={3}
        sx={{
          minHeight: "100vh",
          background: theme.palette.mode === "dark" ? undefined : gradientBg,
        }}
      >
        <MDBox maxWidth={1200} mx="auto" px={2}>
          {/* Hero header */}
          <MDBox
            mb={4}
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            gap={3}
            flexWrap="wrap"
          >
            <MDBox display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(135deg, ${primaryMain}, ${theme.palette.primary.dark || primaryMain})`,
                  boxShadow: `0 8px 24px ${alpha(primaryMain, 0.35)}`,
                }}
              >
                <PsychologyIcon sx={{ color: "white", fontSize: 28 }} />
              </Box>
              <MDBox>
                <MDTypography variant="h3" fontWeight="bold" color="text.primary">
                  Quiz Insights
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  AI-powered analysis of quiz results. Request on demand, get suggestions, and improve teaching.
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Filters + Request */}
          <Card
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              bgcolor: cardBg,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[2],
            }}
          >
            <MDTypography variant="button" fontWeight="bold" color="text.primary" sx={{ display: "block", mb: 2 }}>
              Filters & request
            </MDTypography>
            <MDBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              gap={2}
              flexWrap="wrap"
            >
              <ProfSelector value={profId} onChange={setProfId} includeMyContent />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Scope</InputLabel>
                <Select value={scopeType} label="Scope" onChange={(e) => setScopeType(e.target.value)}>
                  <MenuItem value="all">All results</MenuItem>
                  <MenuItem value="quiz">Single quiz</MenuItem>
                  <MenuItem value="assignment">Assignment</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label="Date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label="Analysis title (optional)"
                placeholder="e.g. Mid-term review"
                value={requestTitle}
                onChange={(e) => setRequestTitle(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <MDButton
                variant="gradient"
                color="primary"
                onClick={handleRequestAnalysis}
                disabled={creating}
                startIcon={creating ? null : <AutoAwesomeIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  boxShadow: `0 4px 14px ${alpha(primaryMain, 0.4)}`,
                }}
              >
                {creating ? "Analyzing…" : "Request new analysis"}
              </MDButton>
            </MDBox>
          </Card>

          {/* List */}
          <MDTypography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 2 }}>
            Your analyses
          </MDTypography>
          {loading ? (
            <MDBox py={4} textAlign="center">
              <MDTypography color="text.secondary">Loading…</MDTypography>
            </MDBox>
          ) : insights.length === 0 ? (
            <Card
              sx={{
                p: 4,
                borderRadius: 3,
                bgcolor: cardBg,
                border: `1px dashed ${theme.palette.divider}`,
                textAlign: "center",
              }}
            >
              <PsychologyIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <MDTypography variant="body1" color="text.secondary">
                No analyses yet. Use the filters above and click &quot;Request new analysis&quot; to get AI suggestions based on your quiz results.
              </MDTypography>
            </Card>
          ) : (
            <MDBox display="flex" flexDirection="column" gap={2}>
              {insights.map((insight) => (
                <Card
                  key={insight.id}
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    bgcolor: cardBg,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[1],
                    transition: "box-shadow 0.2s, border-color 0.2s",
                    "&:hover": {
                      boxShadow: theme.shadows[4],
                      borderColor: alpha(primaryMain, 0.3),
                    },
                  }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ p: 2, cursor: "pointer" }}
                    onClick={() => toggleExpand(insight.id)}
                  >
                    <MDBox display="flex" alignItems="center" gap={2} flex={1} minWidth={0}>
                      <Chip
                        size="small"
                        label={insight.scope_type || "all"}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                      <MDTypography variant="body1" fontWeight="600" color="text.primary" noWrap>
                        {insight.title || "Quiz insight"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        {formatDate(insight.created_at)}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(insight.id)}
                        aria-label="Delete"
                        sx={{ color: "error.main" }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                      <IconButton size="small" aria-label={expandedId === insight.id ? "Collapse" : "Expand"}>
                        {expandedId === insight.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </MDBox>
                  </MDBox>
                  <Collapse in={expandedId === insight.id}>
                    <MDBox
                      sx={{
                        px: 2,
                        pb: 2,
                        pt: 0,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <MDTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          whiteSpace: "pre-wrap",
                          fontFamily: "inherit",
                          lineHeight: 1.7,
                        }}
                      >
                        {insight.summary || "No summary."}
                      </MDTypography>
                    </MDBox>
                  </Collapse>
                </Card>
              ))}
            </MDBox>
          )}
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
